import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import OptimizedCityAutocomplete from '../../../components/OptimizedCityAutocomplete';
import GooglePlacesAutocomplete from '../../../components/GooglePlacesAutocomplete';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import { POST } from '../../../configs/api';
import { api, states } from '../../../utils/constants';
import { useDispatch } from 'react-redux';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { addUser } from '../../../store/slices/usersSlice';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Draggable from 'react-draggable';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InputAdornment from '@mui/material/InputAdornment';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { useTheme } from '@mui/material/styles';
import useIsMobile from '../../../hooks/useIsMobile';

function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

// Phone formatting function
const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX
  if (phoneNumber.length >= 6) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3, 6);
    const secondPart = phoneNumber.substring(6, 10);
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  } else if (phoneNumber.length >= 3) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3);
    return `(${areaCode}) ${firstPart}`;
  } else if (phoneNumber.length > 0) {
    return `(${phoneNumber}`;
  }
  return phoneNumber;
};

// Zip code formatting function
const formatZipCode = (value) => {
  // Remove all non-numeric characters
  const zipCode = value.replace(/\D/g, '');
  
  // Format as 12345-6789 (5+4 format)
  if (zipCode.length > 5) {
    const firstPart = zipCode.substring(0, 5);
    const secondPart = zipCode.substring(5, 9);
    return `${firstPart}-${secondPart}`;
  }
  return zipCode;
};

// SSN formatting function (xxx-xx-xxxx)
const formatSSN = (value) => {
  // Remove all non-numeric characters
  const ssn = value.replace(/\D/g, '');
  
  // Format as xxx-xx-xxxx
  if (ssn.length >= 6) {
    const firstPart = ssn.substring(0, 3);
    const secondPart = ssn.substring(3, 5);
    const thirdPart = ssn.substring(5, 9);
    return `${firstPart}-${secondPart}-${thirdPart}`;
  } else if (ssn.length >= 4) {
    const firstPart = ssn.substring(0, 3);
    const secondPart = ssn.substring(3);
    return `${firstPart}-${secondPart}`;
  } else if (ssn.length > 0) {
    return `${ssn}`;
  }
  return ssn;
};

// EIN formatting function (xx-xxxxxxx)
const formatEIN = (value) => {
  // Remove all non-numeric characters
  const ein = value.replace(/\D/g, '');
  
  // Format as xx-xxxxxxx
  if (ein.length >= 3) {
    const firstPart = ein.substring(0, 2);
    const secondPart = ein.substring(2, 9);
    return `${firstPart}-${secondPart}`;
  } else if (ein.length > 0) {
    return `${ein}`;
  }
  return ein;
};

// Parse initial name into first and last name
const parseInitialName = (initialName) => {
  if (!initialName) return { first_name: '', last_name: '' };
  const nameParts = initialName.trim().split(' ');
  return {
    first_name: nameParts[0] || '',
    last_name: nameParts.slice(1).join(' ') || ''
  };
};

const emptyTechnician = {
  type: 'technician',
  first_name: '',
  last_name: '',
  company_name: '',
  email: '',
  phone: '',
  address: '',
  address_2: '',
  city: '',
  state: '',
  zip: '',
  travel_distance: null,
  latitude: null,
  longitude: null,
  ssn: '',
  ein: '',
  role: 'technician'
};

const emptyErrors = {
  first_name: '',
  last_name: '',
  company_name: '',
  email: '',
  phone: '',
  address: '',
  address_2: '',
  city: '',
  state: '',
  zip: '',
  travel_distance: '',
  ssn: '',
  ein: ''
};

export default function AddTechnicianDialog({ open, onClose, onTechnicianAdded, initialName, initialType = 'technician' }) {
  const theme = useTheme();
  const isSmallScreen = useIsMobile();
  const isDarkMode = theme.palette.mode === 'dark';
  const dispatch = useDispatch();
  const [showError, setShowError] = useState('');
  const [errors, setErrors] = useState(emptyErrors);
  const [newTechnician, setNewTechnician] = useState(emptyTechnician);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [loading, setLoading] = useState(false);

  const isVendor = newTechnician.type === 'vendor';

  // Initialize form with parsed name when dialog opens
  useEffect(() => {
    if (open && initialName) {
      const parsedName = parseInitialName(initialName);
      setNewTechnician({
        ...emptyTechnician,
        type: initialType,
        first_name: parsedName.first_name,
        last_name: parsedName.last_name
      });
    } else if (open) {
      setNewTechnician({
        ...emptyTechnician,
        type: initialType
      });
    }
  }, [open, initialName, initialType]);

  // Handle place selection from Google Places Autocomplete
  const handlePlaceSelect = (addressData) => {
    // Format zip code if provided, otherwise clear it
    const formattedZip = (addressData.zipCode && addressData.zipCode.trim()) 
      ? formatZipCode(addressData.zipCode) 
      : '';
    
    setNewTechnician({
      ...newTechnician,
      address: addressData.address || newTechnician.address,
      // Clear city if not provided, otherwise use the provided value
      city: (addressData.city && addressData.city.trim()) ? addressData.city : '',
      // Clear state if not provided, otherwise use the provided value
      state: (addressData.state && addressData.state.trim()) ? addressData.state : '',
      zip: formattedZip,
      // Store coordinates if available
      latitude: addressData.latitude !== null && addressData.latitude !== undefined ? addressData.latitude : null,
      longitude: addressData.longitude !== null && addressData.longitude !== undefined ? addressData.longitude : null,
    });
  };

  // Add technician API
  const handleAddTechnician = async () => {
    setLoading(true);
    setShowError('');
    setErrors(emptyErrors);
    let newErrors = {};
    
    // Validation based on type
    if (isVendor) {
      // Vendor validation - require company_name
      if (!newTechnician.company_name || newTechnician.company_name.length < 2 || newTechnician.company_name.length > 255) {
        newErrors.company_name = 'Company name must be between 2 and 255 characters';
      }
    } else {
      // Technician validation - require first_name and last_name
      (newTechnician.first_name.length < 2 || newTechnician.first_name.length > 50) && (newErrors.first_name = 'First name must be between 2 and 50 characters');
      (newTechnician.last_name.length < 2 || newTechnician.last_name.length > 50) && (newErrors.last_name = 'Last name must be between 2 and 50 characters');
    }
    
    // Email validation (optional)
    if (newTechnician.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newTechnician.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Phone validation (required)
    if (!newTechnician.phone) {
      newErrors.phone = `Phone is required for ${isVendor ? 'vendors' : 'technicians'}`;
    } else {
      const phoneDigits = newTechnician.phone.replace(/\D/g, '');
      (phoneDigits.length < 10 || phoneDigits.length > 10) && (newErrors.phone = 'Please enter a valid 10-digit US phone number');
    }
    
    // Address validation (optional, but if provided must be valid)
    if (newTechnician.address && newTechnician.address.trim().length > 0 && newTechnician.address.trim().length < 2) {
      newErrors.address = 'Address must be at least 2 characters if provided';
    }
    if (newTechnician.address_2 && newTechnician.address_2.length > 255) {
      newErrors.address_2 = 'Address line 2 must not exceed 255 characters';
    }
    if (newTechnician.city && newTechnician.city.trim().length > 0 && newTechnician.city.trim().length < 2) {
      newErrors.city = 'City must be at least 2 characters if provided';
    }
    if (newTechnician.state && newTechnician.state.trim().length > 0 && newTechnician.state.trim().length < 2) {
      newErrors.state = 'State must be at least 2 characters if provided';
    }
    if (newTechnician.zip && newTechnician.zip.trim().length > 0) {
      const zipRegex = /^\d{5}(-\d{4})?$/;
      (!zipRegex.test(newTechnician.zip)) && (newErrors.zip = 'Please enter a valid US zip code (e.g., 12345 or 12345-6789)');
    }
    
    // Travel distance validation (optional, but if provided must be integer >= 0)
    if (newTechnician.travel_distance !== null && newTechnician.travel_distance !== '') {
      const travelDistance = typeof newTechnician.travel_distance === 'string' ? parseInt(newTechnician.travel_distance, 10) : newTechnician.travel_distance;
      if (isNaN(travelDistance) || travelDistance < 0 || !Number.isInteger(travelDistance)) {
        newErrors.travel_distance = 'Travel distance must be a non-negative integer';
      }
    }
    
    // SSN validation (optional for technicians, but if provided must be valid)
    if (!isVendor && newTechnician.ssn && newTechnician.ssn.trim().length > 0) {
      const ssnDigits = newTechnician.ssn.replace(/\D/g, '');
      if (ssnDigits.length !== 9) {
        newErrors.ssn = 'SSN must be 9 digits (format: xxx-xx-xxxx)';
      }
    }
    
    // EIN validation (optional for vendors, but if provided must be valid)
    if (isVendor && newTechnician.ein && newTechnician.ein.trim().length > 0) {
      const einDigits = newTechnician.ein.replace(/\D/g, '');
      if (einDigits.length !== 9) {
        newErrors.ein = 'EIN must be 9 digits (format: xx-xxxxxxx)';
      }
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      // Convert empty string to null for travel_distance
      // Format SSN/EIN by removing dashes for storage
      const technicianData = {
        ...newTechnician,
        // Clear unused fields based on type
        first_name: isVendor ? '' : newTechnician.first_name,
        last_name: isVendor ? '' : newTechnician.last_name,
        company_name: isVendor ? newTechnician.company_name : '',
        travel_distance: newTechnician.travel_distance === '' || newTechnician.travel_distance === null ? null : parseInt(newTechnician.travel_distance, 10),
        latitude: newTechnician.latitude,
        longitude: newTechnician.longitude,
        // Store SSN/EIN without dashes, clear the other one
        ssn: isVendor ? null : newTechnician.ssn ? newTechnician.ssn.replace(/\D/g, '') : null,
        ein: isVendor ? (newTechnician.ein ? newTechnician.ein.replace(/\D/g, '') : null) : null
      };
      const { status, data, message } = await POST(api.createTechnician, {}, technicianData);
      if (status === 201) {
        setShowSuccessToast(true);
        setNewTechnician(emptyTechnician);
        // Add to Redux with technician role
        dispatch(addUser({ ...data, role: 'technician' }));
        // Call the callback with the new technician
        onTechnicianAdded(data);
        // Close dialog after a short delay to show success
        setTimeout(() => {
          onClose();
        }, 1000);
      } else if (status === 409) {
        setShowError(message || `A ${isVendor ? 'vendor' : 'technician'} with this information already exists.`);
      } else {
        setShowError(data?.message || 'Something went wrong, please try again later.');
      }
    }
    setLoading(false);
  };

  // Clear form errors
  useEffect(() => {
    setErrors(emptyErrors);
    setShowError('');
  }, [newTechnician]);

  // Common TextField sx prop
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      backgroundColor: 'background.paper',
      '& fieldset': {
        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: isDarkMode ? '#475569' : '#bdbdbd',
      },
      '&.Mui-focused fieldset': {
        borderColor: isDarkMode ? '#fff' : '#000',
      }
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'text.secondary',
      '&.Mui-focused': {
        color: 'text.primary',
      }
    },
    '& .MuiFormLabel-asterisk': {
      color: 'error.main',
      fontSize: '1.1em',
      fontWeight: 700,
    },
    '& .MuiInputBase-input': {
      fontSize: '0.875rem',
      color: 'text.primary',
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.75rem',
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      PaperComponent={isSmallScreen ? undefined : PaperComponent}
      aria-labelledby="draggable-dialog-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: isSmallScreen ? 0 : 2,
            width: isSmallScreen ? '100%' : '40vw',
            maxWidth: isSmallScreen ? '100%' : 'none',
            maxHeight: isSmallScreen ? '100%' : '90vh',
            m: isSmallScreen ? 0 : undefined,
          }
        }
      }}
    >
      {/* Header */}
      <Box 
        id="draggable-dialog-title"
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          p: { xs: 2, md: 3 },
          backgroundColor: 'background.paper',
          cursor: isSmallScreen ? 'default' : 'move',
          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'
        }}
      >
        <Typography variant="h6" sx={{ 
          fontWeight: 700, 
          color: 'text.primary',
          fontSize: '1.25rem',
          fontFamily: 'inherit'
        }}>
          Add New {isVendor ? 'Vendor' : 'Technician'}
        </Typography>
        <IconButton 
          onClick={onClose} 
          sx={{ 
            color: 'text.secondary',
            padding: '4px',
            '&:hover': {
              backgroundColor: isDarkMode ? '#1e293b' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Error Message */}
        {showError && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 600 }}>
              {showError}
            </Typography>
          </Box>
        )}

        {/* Form Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* First Name and Last Name on the same line - OR Company Name for vendors */}
          {isVendor ? (
            <TextField
              fullWidth
              required
              label="Company Name (required)"
              value={newTechnician.company_name || ''}
              onChange={(event) => setNewTechnician({ ...newTechnician, company_name: event.target.value })}
              error={!!errors.company_name}
              helperText={errors.company_name}
              sx={textFieldSx}
            />
          ) : (
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <TextField
                sx={{ flex: 1, ...textFieldSx }}
                required
                label="First Name (required)"
                value={newTechnician.first_name || ''}
                onChange={(event) => setNewTechnician({ ...newTechnician, first_name: event.target.value })}
                error={!!errors.first_name}
                helperText={errors.first_name}
              />
              <TextField
                sx={{ flex: 1, ...textFieldSx }}
                required
                label="Last Name (required)"
                value={newTechnician.last_name || ''}
                onChange={(event) => setNewTechnician({ ...newTechnician, last_name: event.target.value })}
                error={!!errors.last_name}
                helperText={errors.last_name}
              />
            </Box>
          )}

          {/* Phone and Email on the same line */}
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <TextField
              sx={{ flex: 1, ...textFieldSx }}
              required
              label="Phone (required)"
              value={newTechnician.phone || ''}
              onChange={(event) => {
                const formattedPhone = formatPhoneNumber(event.target.value);
                setNewTechnician({ ...newTechnician, phone: formattedPhone });
              }}
              error={!!errors.phone}
              helperText={errors.phone}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: '1.2rem' }}>🇺🇸</span>
                      <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>
                        +1
                      </Typography>
                    </Box>
                  </InputAdornment>
                ),
              }}
              placeholder="(555) 123-4567"
            />
            <TextField
              sx={{ flex: 1, ...textFieldSx }}
              label="Email (Optional)"
              type="email"
              value={newTechnician.email || ''}
              onChange={(event) => setNewTechnician({ ...newTechnician, email: event.target.value })}
              error={!!errors.email}
              helperText={errors.email}
              slotProps={{
                input: {
                  autoComplete: 'new-email'
                }
              }}
            />
          </Box>

          {/* Address */}
          <GooglePlacesAutocomplete
            value={newTechnician.address || ''}
            onChange={(value) => setNewTechnician({ ...newTechnician, address: value })}
            onPlaceSelect={handlePlaceSelect}
            error={!!errors.address}
            helperText={errors.address}
            label="Address (Optional)"
            placeholder=""
          />

          <TextField
            label="Address Line 2 (Optional)"
            value={newTechnician.address_2 || ''}
            onChange={(e) => setNewTechnician({ ...newTechnician, address_2: e.target.value })}
            error={!!errors.address_2}
            helperText={errors.address_2}
            placeholder="Suite, unit, floor, etc."
            fullWidth
            sx={textFieldSx}
          />

          {/* City, State, and Zip on the same line */}
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              <OptimizedCityAutocomplete
                value={newTechnician.city || ''}
                onChange={(value) => setNewTechnician({ ...newTechnician, city: value })}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="City (Optional)" 
                    error={!!errors.city}
                    helperText={errors.city}
                  />
                )}
              />
            </Box>

            <Autocomplete
              options={Object.keys(states)}
              value={newTechnician.state || ''}
              onChange={(event, value) => setNewTechnician({ ...newTechnician, state: value })}
              getOptionLabel={(option) => option ? `${states[option]} - ${option}` : ''}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#fff' : '#000',
                  }
                },
                '& .MuiAutocomplete-listbox': {
                  borderRadius: 1,
                  border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                  backgroundColor: 'background.paper',
                  padding: 0,
                }
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li 
                    key={key} 
                    {...optionProps}
                    style={{
                      ...optionProps.style,
                        padding: '8px 16px',
                        margin: 0,
                        backgroundColor: selected ? (isDarkMode ? '#1e293b' : '#f5f5f5') : 'transparent',
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: selected ? 600 : 400,
                          color: 'text.primary',
                          fontSize: '0.875rem',
                        }}
                      >
                      {states[option]} - {option}
                    </Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
              <TextField 
                {...params} 
                label="State (Optional)" 
                placeholder="Select state" 
                fullWidth 
                error={!!errors.state}
                helperText={errors.state}
                      sx={{
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: 'text.secondary',
                          '&.Mui-focused': {
                            color: 'text.primary',
                          }
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem',
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: '0.75rem',
                        }
                      }}
                />
              )}
            />

            <TextField
              sx={{ flex: 1, ...textFieldSx }}
              label="ZIP Code (Optional)"
              value={newTechnician.zip || ''}
              onChange={(event) => {
                const formattedZip = formatZipCode(event.target.value);
                setNewTechnician({ ...newTechnician, zip: formattedZip });
              }}
              error={!!errors.zip}
              helperText={errors.zip}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: '1.2rem' }}>🇺🇸</span>
                    </Box>
                  </InputAdornment>
                ),
              }}
              placeholder="12345 or 12345-6789"
            />
          </Box>

          {/* Type and Travel Distance on the same line */}
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <FormControl sx={{ flex: 1 }} variant="outlined">
              <InputLabel sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: 'text.primary',
                }
              }}>Type</InputLabel>
              <Select
                value={newTechnician.type}
                label="Type"
                onChange={(event) => setNewTechnician({ ...newTechnician, type: event.target.value })}
                sx={{
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  fontSize: '0.875rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#fff' : '#000',
                  }
                }}
              >
                <MenuItem value="technician" sx={{ fontSize: '0.875rem' }}>Technician</MenuItem>
                <MenuItem value="vendor" sx={{ fontSize: '0.875rem' }}>Vendor</MenuItem>
              </Select>
            </FormControl>

            <TextField
              sx={{ flex: 1, ...textFieldSx }}
              label="Travel Distance (miles)"
              type="number"
              value={newTechnician.travel_distance === null ? '' : newTechnician.travel_distance}
              onChange={(event) => {
                const value = event.target.value;
                setNewTechnician({ 
                  ...newTechnician, 
                  travel_distance: value === '' ? null : value 
                });
              }}
              error={!!errors.travel_distance}
              helperText={errors.travel_distance}
              inputProps={{ min: 0, step: 1 }}
              placeholder="e.g., 50"
            />
          </Box>

          {/* SSN - only for technicians */}
          {!isVendor && (
            <TextField
              fullWidth
              label="SSN (Optional)"
              value={newTechnician.ssn || ''}
              onChange={(event) => {
                const formattedSSN = formatSSN(event.target.value);
                setNewTechnician({ ...newTechnician, ssn: formattedSSN });
              }}
              error={!!errors.ssn}
              helperText={errors.ssn || 'Social Security Number (format: XXX-XX-XXXX)'}
              placeholder="xxx-xx-xxxx"
              sx={textFieldSx}
            />
          )}

          {/* EIN - only for vendors */}
          {isVendor && (
            <TextField
              fullWidth
              label="EIN (Optional)"
              value={newTechnician.ein || ''}
              onChange={(event) => {
                const formattedEIN = formatEIN(event.target.value);
                setNewTechnician({ ...newTechnician, ein: formattedEIN });
              }}
              error={!!errors.ein}
              helperText={errors.ein || 'Employer Identification Number (format: XX-XXXXXXX)'}
              placeholder="xx-xxxxxxx"
              sx={textFieldSx}
            />
          )}
        </Box>
      </DialogContent>

      {/* Dialog Actions */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        gap: 2,
        p: 3, 
        borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
        backgroundColor: 'background.paper'
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            borderColor: isDarkMode ? '#334155' : '#e0e0e0',
            color: 'text.primary',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            px: 3,
            py: 1.25,
            borderRadius: 1,
            '&:hover': {
              borderColor: isDarkMode ? '#475569' : '#bdbdbd',
              backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
            }
          }}
        >
          Cancel
        </Button>
        
        <Button 
          variant="contained"
          disabled={loading}
          onClick={handleAddTechnician}
          sx={{ 
            backgroundColor: isDarkMode ? '#fff' : '#000',
            color: isDarkMode ? '#000' : 'white',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            px: 3,
            py: 1.25,
            borderRadius: 1,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: isDarkMode ? '#475569' : '#666',
              color: isDarkMode ? '#94a3b8' : '#fff',
            }
          }}
        >
          {loading ? 'Adding...' : `Add ${isVendor ? 'Vendor' : 'Technician'}`}
        </Button>
      </Box>

      {/* Success Toast */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={3000}
        onClose={() => setShowSuccessToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {isVendor ? 'Vendor' : 'Technician'} added successfully
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
